import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { InteractionDialog } from './InteractionDialog';
import { ListActionDialogs } from './ListActionDialogs';
import { ServicesV4Header } from './ServicesV4Header';
import { useListJobActions } from './useListJobActions';
import { useRecentJobs } from './useRecentJobs';
import { useReportsV4 } from './useReportsV4';
import { useServicesV4Data } from './useServicesV4Data';
import { ActionPermissions, CustomPermission } from '../../@types';
import { PageContainer } from '../../components/atoms/PageContainer';
import { BulkActionsBar } from '../../components/molecules/BulkActionsBar';
import { FilterBar, FilterState } from '../../components/molecules/FilterBar';
import { TablePagination, AutoRefresh } from '../../components/molecules/Pagination';
import { PreviewDrawer } from '../../components/molecules/PreviewDrawer';
import { RecentJobsPanel } from '../../components/molecules/RecentJobs';
import { ReportTabs, SaveReportDialog } from '../../components/molecules/ReportTabs';
import { ServiceTable, SortModel } from '../../components/organisms/ServiceTable';
import { ColumnSelection } from '../../components/templates/ColumnSelection';
import { PLATFORM_NAME } from '../../configs/common';
import { isUserHasPermission } from '../../configs/permissions';
import { TabHandler } from '../../handlers/TabHandler';
import { useExportFile } from '../../queries/uploadedfiles-query';
import { useGetUserSystemPermissions } from '../../queries/user-query';
import { downloadFile } from '../../utils/file-utils';

const EMPTY_FILTERS: FilterState = { statuses: [], jobTypes: [], contracts: '', jobId: '', multiSearch: '', advancedFilters: [] };

/** Inner component -- remounted via key when the path changes. */
const ServicesV4Inner: React.FC = () => {
  const theme = useTheme();
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const getUserPermissionsQuery = useGetUserSystemPermissions();

  useEffect(() => {
    if (getUserPermissionsQuery.data) {
      setPermissions(getUserPermissionsQuery.data);
    }
  }, [getUserPermissionsQuery.data]);

  const {
    data, isFetching, page, pageSize, total, fetchDurationMs,
    sortModel, columns, columnSelectionData, filterModel, statusOptions, statusGroups, jobTypeOptions,
    setPage, setPageSize, setSortModel, applyFilters, applyReportConfig, updateColumnSelection, refetch, multiSearchQuery,
  } = useServicesV4Data();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [filterBarKey, setFilterBarKey] = useState(0);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const [columnSelectionOpen, setColumnSelectionOpen] = useState(false);
  const exportFile = useExportFile();

  const {
    savedReports, activeReport, activeReportName, saveDialogOpen, snackbar,
    handleReportChange, handleSaveNewReport, handleOverwriteReport,
    setSaveDialogOpen, setSnackbar,
  } = useReportsV4({
    filterModel, pageSize, sortModel, columnSelectionData,
    applyFilters, applyReportConfig, setFilters, setFilterBarKey,
  });

  const {
    recentJobs, recentJobsAnchor, recentFilter,
    setRecentJobsAnchor, setRecentFilter,
  } = useRecentJobs();

  const jobActions = useListJobActions(refetch);

  TabHandler(`Jobs | ${PLATFORM_NAME}`);

  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const timer = setInterval(() => refetch(), autoRefreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefreshInterval, refetch]);

  const handleExport = useCallback(async (fileType: 'csv' | 'xlsx') => {
    setExportAnchor(null);
    setSnackbar({ message: 'Export in progress...', severity: 'info' });
    try {
      const visibleCols = columns.filter((c) => c.visible);
      const result = await exportFile.mutateAsync({
        fileType,
        filter: filterModel,
        rowCount: total,
        fileName: 'ServiceList',
        selectedColumns: visibleCols.map((c) => ({ label: c.label, value: c.key })),
        visibleColumnsField: visibleCols.map((c) => c.key),
        sorting: [{ field: sortModel.field, sort: sortModel.sort }],
        pageSize,
        columnSelectionData,
        multiSearchQuery: multiSearchQuery || undefined,
      });
      if (result?.url) {
        downloadFile(result.url, `ServiceList.${fileType}`);
        setSnackbar({ message: 'Export successful', severity: 'success' });
      } else {
        setSnackbar({ message: 'Export completed', severity: 'success' });
      }
    } catch {
      setSnackbar({ message: 'Export failed', severity: 'error' });
    }
  }, [exportFile, filterModel, total, columns, sortModel, pageSize, setSnackbar]);

  const handleSearch = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    applyFilters(newFilters);
  }, [applyFilters]);

  const handleSortChange = useCallback((model: SortModel) => {
    setSortModel({ field: model.field, sort: model.direction });
  }, [setSortModel]);

  const handleBulkAction = useCallback((type: string) => {
    if (type === 'open_tabs') {
      // Open each job in a new tab. Use named windows so repeated clicks
      // reuse the same tabs instead of spawning duplicates.
      for (const id of selectedIds) {
        window.open(`/services/${id}`, `service_${id}`, 'noopener');
      }
      return;
    }
    jobActions.openBulkAction(type, selectedIds);
  }, [selectedIds, jobActions]);

  const handleRowAction = useCallback((id: number, action: string) => {
    if (action === 'preview') { setPreviewId(id); return; }
    jobActions.openRowAction(id, action);
  }, [jobActions]);

  const hasSearchPermission = isUserHasPermission(ActionPermissions.Service_Search, permissions);
  const tableData = useMemo(() => (data as unknown as Record<string, unknown>[]) ?? [], [data]);
  const tableSortModel = useMemo<SortModel>(() => ({
    field: sortModel.field,
    direction: (sortModel.sort as 'asc' | 'desc') ?? 'desc',
  }), [sortModel.field, sortModel.sort]);

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <ServicesV4Header
          total={total}
          permissions={permissions}
          exportAnchor={exportAnchor}
          onExportClick={(e) => setExportAnchor(e.currentTarget)}
          onExportClose={() => setExportAnchor(null)}
          onExportCsv={() => handleExport('csv')}
          onExportExcel={() => handleExport('xlsx')}
          onRecentJobsClick={(e) => setRecentJobsAnchor(e.currentTarget)}
          onColumnsClick={() => setColumnSelectionOpen(true)}
        />
        <ReportTabs
          activeReport={activeReport}
          reports={savedReports}
          onReportChange={handleReportChange}
          onSave={() => setSaveDialogOpen(true)}
        />
        <SaveReportDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onSaveNew={handleSaveNewReport}
          onOverwrite={handleOverwriteReport}
          activeReportName={activeReportName}
        />

        {hasSearchPermission ? (
          <>
            <FilterBar
              key={filterBarKey}
              onSearch={handleSearch}
              statuses={statusOptions}
              statusGroups={statusGroups}
              jobTypes={jobTypeOptions}
              initialFilters={filters}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '3px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '12px', color: 'text.secondary' }}>
                {total > 0 && (
                  <span>Showing {page * pageSize + 1}&ndash;{Math.min((page + 1) * pageSize, total)} of {total.toLocaleString()}</span>
                )}
                {fetchDurationMs !== null && <span>Took {(fetchDurationMs / 1000).toFixed(2)}s</span>}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: 'text.secondary' }}>
                <span>Rows:</span>
                <Select size="small" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page" sx={{ fontSize: '12px', height: 26, '& .MuiSelect-select': { py: 0.25, px: 1 } }}>
                  {[25, 50, 100].map((size) => <MenuItem key={size} value={size} sx={{ fontSize: '12px' }}>{size}</MenuItem>)}
                </Select>
              </Box>
            </Box>
            {selectedIds.length >= 2 && (
              <BulkActionsBar count={selectedIds.length} onAction={handleBulkAction} onClear={() => setSelectedIds([])} />
            )}
            <ServiceTable data={tableData} columns={columns} sortModel={tableSortModel} onSortChange={handleSortChange} selectedIds={selectedIds} onSelectionChange={setSelectedIds} onPreview={(id) => setPreviewId(id)} onRowAction={handleRowAction} loading={isFetching} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', pb: 2 }}>
              <Box sx={{ width: 160 }} />
              <TablePagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
              <AutoRefresh interval={autoRefreshInterval} onChange={setAutoRefreshInterval} />
            </Box>
          </>
        ) : (
          <Typography sx={{ mt: 4, px: '20px', color: theme.palette.text.secondary }}>
            You do not have permission to view services.
          </Typography>
        )}

        <ListActionDialogs
          activeDialog={jobActions.activeDialog}
          targetIds={jobActions.targetIds}
          onClose={jobActions.closeDialog}
          statusOptions={jobActions.statusOptions}
          statusLoading={jobActions.statusLoading}
          onSubmitStatus={jobActions.submitStatus}
          onSubmitAllocation={jobActions.submitAllocation}
          onSubmitSchedule={jobActions.submitSchedule}
          onSubmitNote={jobActions.submitNote}
          submitting={jobActions.submitting}
          lastError={jobActions.lastError}
        />
        <InteractionDialog interactions={jobActions.pendingInteractions} permissions={permissions} onComplete={jobActions.clearPendingInteractions} onCancel={jobActions.cancelPendingInteractions} />
        <PreviewDrawer serviceId={previewId} open={previewId !== null} onClose={() => setPreviewId(null)} />
        <RecentJobsPanel anchorEl={recentJobsAnchor} open={Boolean(recentJobsAnchor)} onClose={() => setRecentJobsAnchor(null)} jobs={recentJobs} filter={recentFilter} onFilterChange={setRecentFilter} />
        <ColumnSelection open={columnSelectionOpen} title="Select Columns" value={columnSelectionData} onChange={updateColumnSelection} onClose={() => setColumnSelectionOpen(false)} />
        <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
            {snackbar?.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageContainer>
  );
};

/** Outer wrapper that remounts the inner component when the route path changes. */
export const ServicesV4: React.FC = () => {
  const location = useLocation();
  return <ServicesV4Inner key={location.pathname} />;
};
